import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../context/AuthContext';
import { FiPackage, FiCalendar, FiDollarSign, FiTruck, FiClock, FiCheckCircle, FiTrash2 } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { getPaymentMethodLabel } from '../utils/paymentLabels';

const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching orders for user:', user.uid);
        
        // Try simple query first
        const ordersQuery = query(
          collection(db, 'orders'),
          where('userId', '==', user.uid)
        );

        const querySnapshot = await getDocs(ordersQuery);
        console.log('Query snapshot size:', querySnapshot.size);
        
        let ordersData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('Order data:', data);
          
          let createdAt = new Date();
          
          // Handle different createdAt formats
          if (data.createdAt) {
            if (data.createdAt.toDate) {
              // Firestore Timestamp
              createdAt = data.createdAt.toDate();
            } else if (data.createdAt instanceof Date) {
              // JavaScript Date
              createdAt = data.createdAt;
            } else if (typeof data.createdAt === 'string') {
              // String date
              createdAt = new Date(data.createdAt);
            }
          }

          return {
            id: doc.id,
            ...data,
            createdAt: createdAt,
            totalAmount: data.total || data.totalAmount || 0
          };
        }).filter(order => !order.deletedByCustomer); // Filter out orders deleted by customer

        // Sort manually by creation date
        ordersData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

        console.log('Processed orders:', ordersData);
        setOrders(ordersData);
      } catch (err) {
        console.error('Error fetching orders:', err);
        console.error('Error details:', {
          message: err.message,
          code: err.code,
          user: user?.uid
        });
        setError(`Failed to load orders: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user]);

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <FiCheckCircle className="h-5 w-5 text-emerald-600" />;
      case 'shipped':
        return <FiTruck className="h-5 w-5 text-sky-600" />;
      case 'confirmed':
        return <FiClock className="h-5 w-5 text-amber-600" />;
      case 'pending':
      default:
        return <FiPackage className="h-5 w-5 text-slate-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-emerald-100 text-emerald-700';
      case 'shipped':
        return 'bg-sky-100 text-sky-700';
      case 'confirmed':
        return 'bg-amber-100 text-amber-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      case 'pending':
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Order Placed';
      case 'confirmed':
        return 'Order Confirmed';
      case 'shipped':
        return 'On The Way';
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Order Placed';
    }
  };

  const handleCancelOrder = async (orderId, orderNumber) => {
    // Show confirmation dialog
    const confirmCancel = window.confirm(
      `Are you sure you want to cancel order #${orderNumber}? This action cannot be undone.`
    );
    
    if (!confirmCancel) return;

    try {
      // Update order status to cancelled
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'cancelled',
        cancelledAt: new Date(),
        updatedAt: new Date()
      });

      // Update local state
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, status: 'cancelled' }
            : order
        )
      );

      toast.success('Order cancelled successfully');
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Failed to cancel order. Please try again.');
    }
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    // Show confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to remove order #${orderNumber} from your order history? This will hide it from your view but admin records will be preserved.`
    );
    
    if (!confirmDelete) return;

    try {
      // Mark order as deleted by customer instead of actually deleting it
      await updateDoc(doc(db, 'orders', orderId), {
        deletedByCustomer: true,
        deletedAt: new Date()
      });

      // Update local state to remove the order from customer view
      setOrders(prevOrders => 
        prevOrders.filter(order => order.id !== orderId)
      );

      toast.success('Order removed from your history successfully');
    } catch (error) {
      console.error('Error removing order:', error);
      toast.error('Failed to remove order. Please try again.');
    }
  };

  const handleShowOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleCloseOrderDetails = () => {
    setSelectedOrder(null);
    setShowOrderDetails(false);
  };

  if (loading) {
    return (
      <div className="sc-container flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="sc-container flex min-h-[60vh] items-center justify-center">
        <div className="sc-soft-card p-8 text-center">
          <div className="mb-4 text-xl text-red-600">{error}</div>
          <button onClick={() => window.location.reload()} className="sc-btn-primary sc-hover-smooth">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sc-container pb-16 pt-8 md:pt-10">
      <nav className="mb-7 flex items-center gap-2 text-sm text-slate-500">
        <Link to="/" className="sc-hover-smooth hover:text-[#DB4444]">
          Home
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-700">Orders</span>
      </nav>

      <section className="sc-soft-card relative mb-6 overflow-hidden p-6 sm:p-7">
        <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-[#DB4444]/10 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#DB4444]/10">
            <FiPackage className="h-6 w-6 text-[#DB4444]" />
          </div>
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Order Center</p>
            <h1 className="text-3xl font-black text-[#1A1A1A]">My Orders</h1>
            <p className="text-slate-600">Track and manage your orders</p>
          </div>
        </div>
      </section>

      {orders.length === 0 ? (
        <div className="sc-soft-card p-12 text-center">
          <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-slate-100">
            <FiPackage className="h-12 w-12 text-slate-400" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-[#1A1A1A]">No orders yet</h2>
          <p className="mb-6 text-slate-600">When you place orders, they will appear here.</p>
          <Link to="/products" className="sc-btn-primary sc-hover-smooth inline-block">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <article key={order.id} className="sc-soft-card overflow-hidden">
              <header className="border-b border-slate-200 bg-slate-50/70 px-6 py-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(order.status)}
                      <span className={`rounded-full px-3 py-1 text-sm font-semibold ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </div>
                    <div className="hidden h-6 w-px bg-slate-300 sm:block" />
                    <div className="text-sm text-slate-600">Order #{order.orderNumber}</div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <FiCalendar className="h-4 w-4" />
                      {order.createdAt.toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <FiDollarSign className="h-4 w-4" />
                      ৳{order.totalAmount?.toFixed(2)}
                    </div>
                  </div>
                </div>
              </header>

              <div className="p-6">
                <div className="space-y-4">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <img
                        src={item.image || item.imageUrl || '/placeholder-product.jpg'}
                        alt={item.name}
                        className="h-16 w-16 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-[#1A1A1A]">{item.name}</h3>
                        <p className="text-sm text-slate-600">Quantity: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#1A1A1A]">৳{(item.price * item.quantity).toFixed(2)}</p>
                        <p className="text-sm text-slate-600">৳{item.price.toFixed(2)} each</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 border-t border-slate-200 pt-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-slate-600">
                        <strong>Payment Method:</strong> {getPaymentMethodLabel(order.paymentMethod)}
                      </p>
                      <p className="text-sm text-slate-600">
                        <strong>Delivery Address:</strong>{' '}
                        {order.customerInfo?.address || order.shippingAddress?.streetAddress
                          ? `${order.shippingAddress?.streetAddress}, ${order.shippingAddress?.apartment}, ${order.shippingAddress?.city}`.replace(
                              /^, |, $/,
                              ''
                            )
                          : 'N/A'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">Total Amount</p>
                      <p className="text-xl font-bold text-[#1A1A1A]">৳{order.totalAmount?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to={`/track-order/${order.id}`} className="sc-btn-primary sc-hover-smooth !rounded-xl !px-4 !py-2 text-sm">
                    Track Order
                  </Link>
                  {order.status?.toLowerCase() === 'pending' && (
                    <button
                      onClick={() => handleCancelOrder(order.id, order.orderNumber)}
                      className="rounded-xl bg-red-600 px-4 py-2 text-sm text-white sc-hover-smooth hover:bg-red-700"
                    >
                      Cancel Order
                    </button>
                  )}
                  {order.status?.toLowerCase() === 'cancelled' && (
                    <button
                      onClick={() => handleDeleteOrder(order.id, order.orderNumber)}
                      className="rounded-xl bg-red-600 px-4 py-2 text-sm text-white sc-hover-smooth hover:bg-red-700 inline-flex items-center gap-2"
                    >
                      <FiTrash2 size={16} />
                      Remove from History
                    </button>
                  )}
                  <button
                    onClick={() => handleShowOrderDetails(order)}
                    className="rounded-xl border border-slate-200 bg-slate-100 px-4 py-2 text-sm text-slate-700 sc-hover-smooth hover:border-[#DB4444] hover:text-[#DB4444]"
                  >
                    Order Details
                  </button>
                </div>

                {order.status?.toLowerCase() === 'cancelled' && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-700">
                      <strong>Order Cancelled:</strong> This order has been cancelled and will not be processed. You can remove it
                      from your order history (admin records will be preserved for business purposes).
                    </p>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      {showOrderDetails && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="sc-soft-card max-h-[90vh] w-full max-w-2xl overflow-y-auto">
            <div className="p-6">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-[#1A1A1A]">Order Details</h2>
                <button onClick={handleCloseOrderDetails} className="text-2xl font-bold text-slate-400 sc-hover-smooth hover:text-slate-600">
                  ×
                </button>
              </div>

              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-[#1A1A1A]">Customer Information</h3>
                <div className="space-y-2 rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
                  <p>
                    <strong>Name:</strong>{' '}
                    {selectedOrder.customerInfo?.name ||
                      selectedOrder.shippingAddress?.firstName + ' ' + selectedOrder.shippingAddress?.lastName ||
                      'N/A'}
                  </p>
                  <p>
                    <strong>Email:</strong> {selectedOrder.customerInfo?.email || selectedOrder.shippingAddress?.email || 'N/A'}
                  </p>
                  <p>
                    <strong>Phone:</strong> {selectedOrder.customerInfo?.phone || selectedOrder.shippingAddress?.phone || 'N/A'}
                  </p>
                  <p>
                    <strong>Address:</strong>{' '}
                    {selectedOrder.customerInfo?.address ||
                      `${selectedOrder.shippingAddress?.streetAddress || ''}, ${selectedOrder.shippingAddress?.apartment || ''}, ${selectedOrder.shippingAddress?.city || ''}`.replace(
                        /^, |, $/,
                        ''
                      ) ||
                      'N/A'}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-[#1A1A1A]">Order Information</h3>
                <div className="space-y-2 rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
                  <p>
                    <strong>Order ID:</strong> {selectedOrder.id}
                  </p>
                  <p>
                    <strong>Order Number:</strong> {selectedOrder.orderNumber}
                  </p>
                  <p>
                    <strong>Total Amount:</strong> ৳
                    {selectedOrder.totalAmount?.toFixed(2) || selectedOrder.total?.toFixed(2) || '0.00'}
                  </p>
                  <p>
                    <strong>Payment Method:</strong> {getPaymentMethodLabel(selectedOrder.paymentMethod)}
                  </p>
                  <p>
                    <strong>Order Date:</strong> {selectedOrder.createdAt?.toLocaleDateString() || 'N/A'}
                  </p>
                  <p>
                    <strong>Status:</strong>
                    <span
                      className={`ml-2 rounded-full px-3 py-1 text-xs font-medium ${
                        selectedOrder.status?.toLowerCase() === 'delivered'
                          ? 'bg-emerald-100 text-emerald-700'
                          : selectedOrder.status?.toLowerCase() === 'shipped'
                            ? 'bg-sky-100 text-sky-700'
                            : selectedOrder.status?.toLowerCase() === 'confirmed'
                              ? 'bg-amber-100 text-amber-700'
                              : selectedOrder.status?.toLowerCase() === 'cancelled'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {selectedOrder.status ? selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1) : 'Pending'}
                    </span>
                  </p>
                </div>
              </div>

              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="mb-6">
                  <h3 className="mb-4 text-lg font-semibold text-[#1A1A1A]">Order Items</h3>
                  <div className="rounded-lg bg-slate-50 p-4">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between border-b border-slate-200 py-2 last:border-b-0">
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{item.name || item.title}</p>
                          <p className="text-sm text-slate-600">Quantity: {item.quantity}</p>
                        </div>
                        <p className="font-medium text-[#1A1A1A]">৳{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button onClick={handleCloseOrderDetails} className="sc-btn-ghost sc-hover-smooth">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
